/**
 * /vaccines/by-age/<slug> — age cohort page.
 *
 * Shows every vaccine scheduled at this age, side-by-side across countries.
 * High-value SEO page — long-tail parent searches like "what vaccines at
 * 8 weeks" land here.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Calendar, Syringe, ArrowLeft, ArrowRight } from "lucide-react";
import api from "../../lib/api";
import SEO from "../../components/SEO";
import SaveButton from "../../components/research/SaveButton";
import CiteAndPrint from "../../components/research/CiteAndPrint";
import { AGE_COHORTS, cohortBySlug, COUNTRIES } from "../../lib/vaccineCohorts";

export default function AgeCohortPage() {
    const { slug } = useParams();
    const cohort = cohortBySlug(slug);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!cohort) { setLoading(false); return; }
        api.get(`/schedules/by-age/${cohort.months}`)
            .then(({ data }) => setRows(data.rows || []))
            .catch(() => setRows([]))
            .finally(() => setLoading(false));
    }, [cohort]);

    if (!cohort) return <Navigate to="/vaccines" replace />;

    const byCountry = COUNTRIES.reduce((acc, c) => {
        acc[c.code] = rows.filter((r) => r.country === c.code);
        return acc;
    }, {});

    const idx = AGE_COHORTS.findIndex((c) => c.slug === cohort.slug);
    const prev = idx > 0 ? AGE_COHORTS[idx - 1] : null;
    const next = idx < AGE_COHORTS.length - 1 ? AGE_COHORTS[idx + 1] : null;

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Dataset",
        name: `Vaccines scheduled at ${cohort.label}`,
        description: `UK and US vaccines on the routine schedule at ${cohort.label}.`,
        url: `https://library.thecontextproject.com/vaccines/by-age/${cohort.slug}`,
    };

    return (
        <div className="min-h-screen bg-[#0B1220] text-[#CBD5E1]" data-testid={`age-cohort-${cohort.slug}`}>
            <SEO
                title={`Vaccines at ${cohort.label} — UK & US schedules`}
                description={`Every vaccine scheduled at ${cohort.label} on the UK and US routine schedules, with linked research and evidence.`}
                canonicalPath={`/vaccines/by-age/${cohort.slug}`}
                jsonLd={jsonLd}
            />
            <div className="h-px bg-gradient-to-r from-transparent via-[#56CCF2]/70 to-transparent" />

            <nav className="max-w-5xl mx-auto px-6 pt-6 text-[11px] text-[#64748B]" aria-label="Breadcrumb">
                <Link to="/" className="hover:text-[#56CCF2]">Library</Link>
                <span className="mx-1.5">›</span>
                <Link to="/vaccines" className="hover:text-[#56CCF2]">Vaccines</Link>
                <span className="mx-1.5">›</span>
                <span className="text-[#94A3B8]">{cohort.label}</span>
            </nav>

            <header className="max-w-5xl mx-auto px-6 pt-8 pb-10">
                <div className="flex items-start justify-between gap-6">
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2.5 text-[10px] uppercase tracking-[0.28em] text-[#56CCF2] font-semibold mb-2.5">
                            <Calendar className="w-3.5 h-3.5" />
                            Age cohort
                        </div>
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.05] mb-4">
                            Vaccines at {cohort.label}
                        </h1>
                        <p className="text-[15px] text-[#94A3B8] leading-relaxed max-w-3xl">
                            Every vaccine scheduled at {cohort.label} on the UK and US routine schedules.
                            Click any vaccine to explore its research record, ingredients, manufacturer and connected investigations.
                        </p>
                    </div>
                    <div className="shrink-0">
                        <SaveButton
                            kind="vaccine_age_cohort"
                            refSlug={cohort.slug}
                            refTitle={`Vaccines at ${cohort.label}`}
                            refUrl={`/vaccines/by-age/${cohort.slug}`}
                            variant="pill"
                        />
                    </div>
                </div>
                <div className="mt-6">
                    <CiteAndPrint
                        title={`Vaccines at ${cohort.label}`}
                        canonicalPath={`/vaccines/by-age/${cohort.slug}`}
                        slug={`age-${cohort.slug}`}
                    />
                </div>
            </header>

            {loading ? (
                <div className="text-center text-[13px] text-[#64748B] py-10">Loading…</div>
            ) : (
                <section className="max-w-5xl mx-auto px-6 pb-12 grid grid-cols-1 md:grid-cols-2 gap-5">
                    {COUNTRIES.map((c) => (
                        <div key={c.code} className="bg-[#111827] border border-[#243044] rounded-xl p-5" data-testid={`cohort-country-${c.code}`}>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[#56CCF2] font-semibold mb-3">{c.label}</div>
                            {byCountry[c.code].length === 0 ? (
                                <div className="text-[12px] text-[#64748B] italic py-4">No vaccines scheduled at {cohort.label} for {c.label}.</div>
                            ) : (
                                <div className="space-y-2">
                                    {byCountry[c.code].map((r) => (
                                        <Link
                                            key={r.id}
                                            to={`/vaccines/v/${r.vaccine_slug}`}
                                            className="group flex items-center gap-3 bg-[#0B1220] border border-[#243044] hover:border-[#56CCF2]/50 rounded-md px-3 py-2 transition-colors"
                                        >
                                            <Syringe className="w-3.5 h-3.5 text-[#56CCF2]" />
                                            <div className="min-w-0 flex-1">
                                                <div className="text-sm text-white group-hover:text-[#56CCF2] transition-colors truncate">{r.vaccine_display_name}</div>
                                                <div className="text-[10px] text-[#64748B] font-mono">Dose {r.dose_number}/{r.total_doses}</div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </section>
            )}

            {/* Prev / Next cohort */}
            <section className="max-w-5xl mx-auto px-6 pb-16 flex items-center justify-between text-xs">
                {prev ? (
                    <Link to={`/vaccines/by-age/${prev.slug}`} className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors" data-testid="cohort-prev">
                        <ArrowLeft className="w-3.5 h-3.5" /> {prev.label}
                    </Link>
                ) : <span />}
                {next ? (
                    <Link to={`/vaccines/by-age/${next.slug}`} className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-white transition-colors" data-testid="cohort-next">
                        {next.label} <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                ) : <span />}
            </section>
        </div>
    );
}
